/* ED Fertilizer Lawn Guide v2.1 - starter fertilizer behavior support */
(function () {
  'use strict';

  const GLOBAL_KEY = '__edFertilizerLawnGuide';

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].initAll();
    return;
  }

  const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  const STATES = [
    ['Alabama', 'AL'],
    ['Alaska', 'AK'],
    ['Arizona', 'AZ'],
    ['Arkansas', 'AR'],
    ['California', 'CA'],
    ['Colorado', 'CO'],
    ['Connecticut', 'CT'],
    ['Delaware', 'DE'],
    ['District of Columbia', 'DC'],
    ['Florida', 'FL'],
    ['Georgia', 'GA'],
    ['Hawaii', 'HI'],
    ['Idaho', 'ID'],
    ['Illinois', 'IL'],
    ['Indiana', 'IN'],
    ['Iowa', 'IA'],
    ['Kansas', 'KS'],
    ['Kentucky', 'KY'],
    ['Louisiana', 'LA'],
    ['Maine', 'ME'],
    ['Maryland', 'MD'],
    ['Massachusetts', 'MA'],
    ['Michigan', 'MI'],
    ['Minnesota', 'MN'],
    ['Mississippi', 'MS'],
    ['Missouri', 'MO'],
    ['Montana', 'MT'],
    ['Nebraska', 'NE'],
    ['Nevada', 'NV'],
    ['New Hampshire', 'NH'],
    ['New Jersey', 'NJ'],
    ['New Mexico', 'NM'],
    ['New York', 'NY'],
    ['North Carolina', 'NC'],
    ['North Dakota', 'ND'],
    ['Ohio', 'OH'],
    ['Oklahoma', 'OK'],
    ['Oregon', 'OR'],
    ['Pennsylvania', 'PA'],
    ['Rhode Island', 'RI'],
    ['South Carolina', 'SC'],
    ['South Dakota', 'SD'],
    ['Tennessee', 'TN'],
    ['Texas', 'TX'],
    ['Utah', 'UT'],
    ['Vermont', 'VT'],
    ['Virginia', 'VA'],
    ['Washington', 'WA'],
    ['West Virginia', 'WV'],
    ['Wisconsin', 'WI'],
    ['Wyoming', 'WY']
  ].map(([name, code]) => ({ name, code }));

  const STATE_LOOKUP = new Map();
  STATES.forEach((state) => {
    STATE_LOOKUP.set(normalize(state.name), state);
    STATE_LOOKUP.set(normalize(state.code), state);
  });

  function normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function slugify(value) {
    return normalize(value).replace(/\s+/g, '-');
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function escapeAttribute(value) {
    return escapeHtml(value).replace(/`/g, '&#096;');
  }

  function parseJsonScript(root, selector, fallback) {
    const node = root.querySelector(selector);
    if (!node) return fallback;

    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      console.warn('[Lawn Guide] Unable to parse section data.', error);
      return fallback;
    }
  }

  function parseStateCodes(value) {
    return new Set(
      String(value || '')
        .split(/[\s,;|]+/)
        .map((code) => code.trim().toUpperCase())
        .filter(Boolean)
    );
  }

  function stateFromValue(value) {
    return STATE_LOOKUP.get(normalize(value)) || null;
  }

  function scalarValue(value) {
    if (value == null) return '';
    if (Array.isArray(value)) return value.length ? scalarValue(value[0]) : '';
    if (typeof value === 'object') {
      return scalarValue(value.value || value.name || value.label || value.key || '');
    }
    return String(value).trim();
  }

  function monthFromText(value) {
    const text = normalize(scalarValue(value));
    if (!text) return '';

    return MONTHS.find((month) => {
      const full = normalize(month);
      const short = full.slice(0, 3);
      return text.split(' ').includes(full) || text.split(' ').includes(short);
    }) || '';
  }

  function normalizedMonth(value) {
    const direct = scalarValue(value);
    return MONTHS.find((month) => normalize(month) === normalize(direct)) || monthFromText(direct);
  }

  function normalizedMonthList(value) {
    const values = Array.isArray(value) ? value : (value == null || value === '' ? [] : [value]);
    return values
      .map((item) => normalizedMonth(item))
      .filter((month, index, all) => month && all.indexOf(month) === index);
  }

  function monthNumber(value) {
    const normalized = normalizedMonth(value);
    const index = MONTHS.findIndex((month) => month === normalized);
    return index >= 0 ? index + 1 : 0;
  }

  function monthAbbreviation(value) {
    const month = normalizedMonth(value);
    return month ? month.slice(0, 3).toUpperCase() : '';
  }

  function monthFromStepCopy(step) {
    if (!step) return '';
    return monthFromText([
      step.name,
      step.customerTitle,
      step.applicationTiming,
      step.description
    ].filter(Boolean).join(' '));
  }

  function primaryMonthForStep(step) {
    if (!step) return '';

    const direct = normalizedMonth(step.month);
    if (direct) return direct;

    const active = normalizedMonthList(step.activeMonths);
    if (active.length) return active[0];

    return monthFromStepCopy(step);
  }

  function monthLabelForStep(step, abbreviated) {
    if (!step) return '';

    const direct = normalizedMonth(step.month);
    if (direct) return abbreviated ? monthAbbreviation(direct) : direct;

    const active = normalizedMonthList(step.activeMonths);
    if (active.length === 1) return abbreviated ? monthAbbreviation(active[0]) : active[0];
    if (active.length > 1) {
      const first = abbreviated ? monthAbbreviation(active[0]) : active[0];
      const last = abbreviated ? monthAbbreviation(active[active.length - 1]) : active[active.length - 1];
      return `${first}\u2013${last}`;
    }

    const copyMonth = monthFromStepCopy(step);
    return copyMonth ? (abbreviated ? monthAbbreviation(copyMonth) : copyMonth) : '';
  }

  function initGuide(root) {
    if (!root || root.dataset.edLawnGuideReady === 'true') return;

    const data = parseJsonScript(root, '[data-ed-lg-data]', { programs: [], currentProduct: null });
    const config = parseJsonScript(root, '[data-ed-lg-config]', {});
    const screens = Array.from(root.querySelectorAll('[data-ed-lg-screen]'));
    const grassButtons = Array.from(root.querySelectorAll('[data-ed-lg-option]'));
    const progress = root.querySelector('[data-ed-lg-progress]');
    const progressLabel = root.querySelector('[data-ed-lg-progress-label]');
    const progressBars = Array.from(root.querySelectorAll('[data-ed-lg-progress-bar]'));
    const stateInput = root.querySelector('[data-ed-lg-state-input]');
    const stateError = root.querySelector('[data-ed-lg-state-error]');
    const stateSubmit = root.querySelector('[data-ed-lg-state-submit]');
    const stateQuickButtons = Array.from(root.querySelectorAll('[data-ed-lg-state-quick]'));
    const resultMount = root.querySelector('[data-ed-lg-result]');
    const storageKey = 'ed-lawn-guide-selection-v1';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!screens.length || !grassButtons.length || !stateInput || !stateSubmit || !resultMount) return;

    root.dataset.edLawnGuideReady = 'true';

    const regionStateSets = {
      Northern: parseStateCodes(config.regions && config.regions.northern),
      'Transition Zone': parseStateCodes(config.regions && config.regions.transition),
      Southern: parseStateCodes(config.regions && config.regions.southern),
      Florida: parseStateCodes(config.regions && config.regions.florida)
    };

    const options = grassButtons.map((button) => ({
      button,
      key: button.dataset.optionKey || slugify(button.dataset.label),
      label: button.dataset.label || '',
      description: button.dataset.description || '',
      behavior: button.dataset.behavior || 'program',
      matchNames: String(button.dataset.matchGrass || '')
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean),
      preferredProgram: button.dataset.preferredProgram || ''
    }));

    let selectedOption = null;
    let selectedState = null;
    let screenTimer = null;
    let stateTimer = null;

    function trackGuideEvent(eventName, details) {
      if (window.Shopify && window.Shopify.designMode) return;

      const payload = Object.assign({
        section_id: root.id || '',
        current_product_id: productId(data.currentProduct),
        current_product_title: data.currentProduct ? data.currentProduct.title : '',
        page_path: window.location.pathname
      }, details || {});

      try {
        window.dispatchEvent(new CustomEvent('lawn-synergy:analytics', {
          detail: Object.assign({ event: eventName }, payload)
        }));

        if (typeof window.gtag === 'function') {
          window.gtag('event', eventName, payload);
        } else {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(Object.assign({ event: eventName }, payload));
        }
      } catch (error) {
        console.warn('[Lawn Guide] Unable to record analytics event.', error);
      }
    }

    function currentMonthNumber() {
      const override = Number(config.previewMonth || 0);
      if (override >= 1 && override <= 12) return override;
      return new Date().getMonth() + 1;
    }

    function currentMonthName() {
      return MONTHS[currentMonthNumber() - 1];
    }

    function regionForState(code) {
      const stateCode = String(code || '').toUpperCase();
      if (regionStateSets.Florida.has(stateCode)) return 'Florida';
      if (regionStateSets['Transition Zone'].has(stateCode)) return 'Transition Zone';
      if (regionStateSets.Southern.has(stateCode)) return 'Southern';
      if (regionStateSets.Northern.has(stateCode)) return 'Northern';
      return '';
    }

    function updateProgress(screenName) {
      const isQuestion = screenName === 'grass' || screenName === 'state';
      if (progress) progress.hidden = !isQuestion || config.showProgress === false;

      if (!isQuestion) return;

      const step = screenName === 'grass' ? 1 : 2;
      if (progressLabel) progressLabel.textContent = `Step ${step} of 2`;
      progressBars.forEach((bar, index) => {
        bar.classList.toggle('is-active', index < step);
      });
    }

    function focusScreen(screen) {
      const heading = screen && screen.querySelector('[data-ed-lg-focus]');
      if (!heading) return;
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
    }

    function isMobileGuideViewport() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    function visibleStickyHeaderHeight() {
      const stickyHeader = document.querySelector('.shopify-section-header-sticky, sticky-header');
      if (!stickyHeader) return 0;

      const bounds = stickyHeader.getBoundingClientRect();
      return bounds.bottom > 0 && bounds.top <= 0 ? bounds.height : 0;
    }

    function alignGuideToViewport() {
      if (!isMobileGuideViewport()) return;

      const wizard = root.querySelector('.ed-fertilizer-guide__wizard');
      if (!wizard) return;

      const targetTop = window.scrollY + wizard.getBoundingClientRect().top - visibleStickyHeaderHeight() - 12;
      if (Math.abs(window.scrollY - targetTop) <= 2) return;

      window.scrollTo({ left: window.scrollX, top: Math.max(0, targetTop), behavior: 'auto' });
    }

    function showScreen(name, optionsArg) {
      const opts = optionsArg || {};
      const next = screens.find((screen) => screen.dataset.edLgScreen === name);
      const current = screens.find((screen) => screen.classList.contains('is-active'));
      if (!next || current === next) return;

      if (screenTimer) window.clearTimeout(screenTimer);

      const complete = () => {
        screens.forEach((screen) => {
          const active = screen === next;
          screen.hidden = !active;
          screen.classList.toggle('is-active', active);
        });

        root.classList.toggle('is-result', name === 'result');
        root.classList.toggle('is-state', name === 'state');
        updateProgress(name);

        if (opts.alignMobile) {
          alignGuideToViewport();
        }

        if (!opts.skipFocus) {
          window.requestAnimationFrame(() => focusScreen(next));
        }
      };

      if (!current || reducedMotion || opts.immediate || isMobileGuideViewport()) {
        complete();
        return;
      }

      current.classList.remove('is-active');
      screenTimer = window.setTimeout(complete, 150);
    }

    function saveSelection() {
      if (!config.rememberAnswers || !selectedOption || !selectedState) return;

      try {
        window.localStorage.setItem(
          storageKey,
          JSON.stringify({ optionKey: selectedOption.key, stateCode: selectedState.code })
        );
      } catch (error) {
        // Local storage can be unavailable in private browsing. The guide still works without it.
      }
    }

    function clearSavedSelection() {
      try {
        window.localStorage.removeItem(storageKey);
      } catch (error) {
        // No action needed.
      }
    }

    function compatibleGrassNames(program) {
      return (program.compatibleGrass || []).map((grass) => normalize(grass.name));
    }

    function resolveProgram(option, state) {
      const programs = Array.isArray(data.programs) ? data.programs : [];
      const preferred = normalize(option.preferredProgram);
      const desiredNames = new Set(option.matchNames.map(normalize));
      const region = regionForState(state.code);

      let candidates = programs.filter((program) => {
        const names = compatibleGrassNames(program);
        return names.some((name) => desiredNames.has(name));
      });

      if (preferred) {
        const preferredMatch = candidates.find((program) => {
          const programName = normalize(program.name);
          return programName === preferred || programName.includes(preferred) || preferred.includes(programName);
        }) || programs.find((program) => {
          const programName = normalize(program.name);
          return programName === preferred || programName.includes(preferred) || preferred.includes(programName);
        });

        if (preferredMatch) return preferredMatch;
      }

      const exactRegion = candidates.find((program) => normalize(program.region) === normalize(region));
      if (exactRegion) return exactRegion;

      if (candidates.length === 1) return candidates[0];

      if (desiredNames.has(normalize('Tall Fescue'))) {
        const transitionFallback = candidates.find(
          (program) => normalize(program.region) === normalize('Transition Zone')
        );
        if (transitionFallback) return transitionFallback;
      }

      const sameFamilyRegion = candidates.find((program) => {
        if (region === 'Florida' && normalize(program.region) === normalize('Southern')) return true;
        if (region === 'Southern' && normalize(program.region) === normalize('Florida')) return true;
        return false;
      });

      return sameFamilyRegion || candidates[0] || null;
    }

    function isTropicalStep(step) {
      const searchable = normalize([
        step.name,
        step.customerTitle,
        step.applicationTiming,
        step.description
      ].filter(Boolean).join(' '));

      return searchable.includes('tropical') || searchable.includes('south florida');
    }

    function applicableSteps(program, state) {
      const stateRegion = regionForState(state.code);
      return (program.steps || []).filter((step) => {
        if (!isTropicalStep(step)) return true;
        return stateRegion === 'Florida';
      });
    }

    function activeMonthsForStep(step) {
      const active = normalizedMonthList(step && step.activeMonths);
      if (active.length) return active;
      const primary = primaryMonthForStep(step);
      return primary ? [primary] : [];
    }

    function findCurrentStep(steps, monthName) {
      const matches = steps.filter((step) =>
        activeMonthsForStep(step).some((month) => normalize(month) === normalize(monthName))
      );

      if (!matches.length) return null;
      return matches.find((step) => normalize(primaryMonthForStep(step)) === normalize(monthName)) || matches[0];
    }

    function findNextStep(steps, currentStep, monthName) {
      if (!steps.length) return null;

      if (currentStep) {
        const index = steps.findIndex((step) => step.id === currentStep.id);
        if (index >= 0) return steps[(index + 1) % steps.length];
      }

      const currentNumber = monthNumber(monthName);
      const future = steps
        .map((step, index) => ({ step, index, number: monthNumber(primaryMonthForStep(step)) }))
        .filter((item) => item.number > currentNumber)
        .sort((a, b) => a.number - b.number || a.index - b.index);

      if (future.length) return future[0].step;

      return steps
        .map((step, index) => ({ step, index, number: monthNumber(primaryMonthForStep(step)) }))
        .sort((a, b) => a.number - b.number || a.index - b.index)[0].step;
    }

    function productId(product) {
      return product && product.id != null ? String(product.id) : '';
    }

    function isSamePageUrl(value) {
      if (!value) return false;

      try {
        const target = new URL(value, window.location.href);
        const current = new URL(window.location.href);
        const cleanPath = (path) => String(path || '/').replace(/\/+$/, '') || '/';

        return target.origin === current.origin && cleanPath(target.pathname) === cleanPath(current.pathname);
      } catch (error) {
        return false;
      }
    }

    function currentProductStatus(step) {
      const currentProduct = data.currentProduct || null;
      const currentId = productId(currentProduct);
      const primaryId = productId(step && step.product);
      const currentBehavior = normalize(currentProduct && currentProduct.behavior);

      if (!currentId) return { type: 'guide', displayProduct: step.product, alternative: null };
      if (currentBehavior === 'starter') {
        return { type: 'starter', displayProduct: step.product, alternative: null };
      }
      if (currentId && currentId === primaryId) {
        return { type: 'correct', displayProduct: step.product, alternative: null };
      }

      const alternative = (step.alternativeProducts || []).find((product) => productId(product) === currentId);
      if (alternative) {
        return { type: 'acceptable', displayProduct: currentProduct, alternative };
      }

      return { type: 'different', displayProduct: step.product, alternative: null };
    }

    function findCurrentProductStep(steps) {
      const currentId = productId(data.currentProduct);
      if (!currentId) return null;
      return steps.find((step) => productId(step.product) === currentId) || null;
    }

    function renderProductImage(product) {
      if (!config.showProductImage || !product || !product.image) return '';
      return `
        <div class="ed-fertilizer-guide__product-media">
          <img src="${escapeAttribute(product.image)}" alt="${escapeAttribute(product.title || '')}" loading="lazy" width="360" height="360">
        </div>
      `;
    }

    function renderActions(status, step) {
      if (!status.displayProduct) return '';

      const trackingAttributes = `data-ed-lg-result-status="${escapeAttribute(status.type)}" data-ed-lg-product-id="${escapeAttribute(productId(status.displayProduct))}" data-ed-lg-product-title="${escapeAttribute(status.displayProduct.title || '')}"`;

      if (status.type === 'correct') {
        return `
          <div class="ed-fertilizer-guide__actions">
            <button type="button" class="ed-fertilizer-guide__button" data-ed-lg-add-current ${trackingAttributes}>
              ${escapeHtml(config.correctCtaLabel || 'Add to Cart')}
            </button>
          </div>
        `;
      }

      if (status.type === 'acceptable') {
        const primaryLink = step.product && step.product.url && !isSamePageUrl(step.product.url)
          ? `<a class="ed-fertilizer-guide__button ed-fertilizer-guide__button--secondary" data-ed-lg-product-link href="${escapeAttribute(step.product.url)}">View Primary Recommendation</a>`
          : '';

        return `
          <div class="ed-fertilizer-guide__actions">
            <button type="button" class="ed-fertilizer-guide__button" data-ed-lg-add-current ${trackingAttributes}>
              ${escapeHtml(config.correctCtaLabel || 'Add to Cart')}
            </button>
            ${primaryLink.replace('data-ed-lg-product-link', `data-ed-lg-product-link ${trackingAttributes}`)}
          </div>
        `;
      }

      if (status.displayProduct.url && !isSamePageUrl(status.displayProduct.url)) {
        return `
          <div class="ed-fertilizer-guide__actions">
            <a class="ed-fertilizer-guide__button" data-ed-lg-product-link ${trackingAttributes} href="${escapeAttribute(status.displayProduct.url)}">
              ${escapeHtml(config.differentCtaLabel || 'View Recommended Product')}
            </a>
          </div>
        `;
      }

      return '';
    }

    function renderStatus(status, headline = false, note = '') {
      let message = '';

      if (status.type === 'correct') {
        message = config.correctHeading || 'Yes - this is the right fertilizer for your lawn now.';
      } else if (status.type === 'acceptable') {
        message = config.acceptableHeading || 'This product is a good alternative for your lawn right now.';
      } else if (status.type === 'starter') {
        message = config.starterHeading || "Installing new seed or sod? You're in the right place.";
      } else if (status.type === 'different') {
        message = config.differentStatusHeading || 'We recommend a different fertilizer for your lawn right now.';
      }

      if (!message) return '';

      const statusNote = status.type === 'starter'
        ? (config.starterText || "Great for new seed, overseeding, sod, or increasing nitrogen, phosphorus, and potassium all at once.")
        : note;
      const classes = [
        'ed-fertilizer-guide__status',
        headline ? 'ed-fertilizer-guide__status--headline' : '',
        status.type === 'different' ? 'is-warning' : '',
        status.type === 'starter' ? 'is-starter' : ''
      ].filter(Boolean).join(' ');

      return `
        <div class="${classes}" role="status" aria-live="polite">
          <span class="ed-fertilizer-guide__status-copy">
            <span class="ed-fertilizer-guide__status-message">${escapeHtml(message)}</span>
            ${statusNote ? `<span class="ed-fertilizer-guide__status-note">${escapeHtml(statusNote)}</span>` : ''}
          </span>
        </div>
      `;
    }

    function renderAlternatives(step, status) {
      const alternatives = Array.isArray(step.alternativeProducts) ? step.alternativeProducts : [];
      const links = [];

      if (status.type === 'acceptable' && step.product) {
        links.push(step.product);
      }

      alternatives.forEach((product) => {
        if (productId(product) !== productId(status.displayProduct)) links.push(product);
      });

      const unique = links.filter((product, index, all) =>
        all.findIndex((candidate) => productId(candidate) === productId(product)) === index
      );

      if (!unique.length && !step.alternativeDescription) return '';

      const linkHtml = unique.map((product) => {
        if (!product.url || isSamePageUrl(product.url)) return `<span>${escapeHtml(product.title)}</span>`;
        return `<a data-ed-lg-product-link href="${escapeAttribute(product.url)}">${escapeHtml(product.title)}</a>`;
      }).join('');

      return `
        <details class="ed-fertilizer-guide__alternatives">
          <summary role="button" aria-expanded="false">
            <span>Other options</span>
            ${renderDisclosureIcon()}
          </summary>
          <div class="ed-fertilizer-guide__alternative-body">
            ${step.alternativeDescription ? `<p>${escapeHtml(step.alternativeDescription)}</p>` : ''}
            ${linkHtml ? `<div class="ed-fertilizer-guide__alternative-links">${linkHtml}</div>` : ''}
          </div>
        </details>
      `;
    }

    function renderNextStep(step) {
      if (!config.showNextStep || !step) return '';
      const product = step.product || {};
      const canNavigate = Boolean(product.url && !isSamePageUrl(product.url));
      const content = `
        <span class="ed-fertilizer-guide__next-copy">
          <span class="ed-fertilizer-guide__next-label">Next application</span>
          <span class="ed-fertilizer-guide__next-title">${escapeHtml(product.title || step.customerTitle || step.name)}</span>
        </span>
        <span class="ed-fertilizer-guide__next-meta">
          <span class="ed-fertilizer-guide__next-month">${escapeHtml(monthLabelForStep(step, false))}</span>
          ${canNavigate ? '<span class="ed-fertilizer-guide__next-arrow">\u2192</span>' : ''}
        </span>
      `;

      if (canNavigate) {
        return `<a class="ed-fertilizer-guide__next" data-ed-lg-product-link href="${escapeAttribute(product.url)}">${content}</a>`;
      }

      return `<div class="ed-fertilizer-guide__next is-static">${content}</div>`;
    }

    function renderDisclosureIcon() {
      return `
        <span class="ed-fertilizer-guide__disclosure-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" role="presentation" focusable="false">
            <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"/>
          </svg>
        </span>
      `;
    }

    function renderYearPlan(steps, currentStep) {
      if (!config.showYearPlan || !steps.length) return '';

      const items = steps.map((step, index) => {
        const monthLabel = monthLabelForStep(step, true) || `R${index + 1}`;
        return `
          <article class="ed-fertilizer-guide__timeline-step${currentStep && currentStep.id === step.id ? ' is-current' : ''}" role="listitem">
            <span class="ed-fertilizer-guide__timeline-month" aria-label="${escapeAttribute(monthLabelForStep(step, false) || `Round ${index + 1}`)}">${escapeHtml(monthLabel)}</span>
            <strong>${escapeHtml(step.customerTitle || step.name)}</strong>
            <span>${escapeHtml(step.product ? step.product.title : '')}</span>
          </article>
        `;
      }).join('');

      return `
        <div class="ed-fertilizer-guide__full-plan">
          <p class="ed-fertilizer-guide__full-plan-title">Full-year plan</p>
          <div class="ed-fertilizer-guide__timeline" role="list" style="--ed-lg-timeline-count:${Math.max(steps.length, 1)}">${items}</div>
        </div>
      `;
    }

    function renderPlanDetails(steps, currentStep, nextStep) {
      const nextStepHtml = renderNextStep(nextStep);
      const yearPlanHtml = renderYearPlan(steps, currentStep);
      if (!nextStepHtml && !yearPlanHtml) return '';

      return `
        <details class="ed-fertilizer-guide__year" data-ed-lg-year-plan>
          <summary role="button" aria-expanded="false">
            <span>${escapeHtml(config.fullPlanLabel || 'View Lawn Plan')}</span>
            ${renderDisclosureIcon()}
          </summary>
          <div class="ed-fertilizer-guide__plan-details">
            ${nextStepHtml}
            ${yearPlanHtml}
          </div>
        </details>
      `;
    }

    function renderProgramCta() {
      if (!config.showProgramCta || !config.programCtaLink) return '';

      return `
        <aside class="ed-fertilizer-guide__program-cta">
          <div class="ed-fertilizer-guide__program-copy">
            ${config.programCtaHeading ? `<p class="ed-fertilizer-guide__program-heading">${escapeHtml(config.programCtaHeading)}</p>` : ''}
            ${config.programCtaText ? `<p>${escapeHtml(config.programCtaText)}</p>` : ''}
          </div>
          ${config.programCtaLabel ? `
            <a class="ed-fertilizer-guide__program-link" href="${escapeAttribute(config.programCtaLink)}">
              ${escapeHtml(config.programCtaLabel)}
              <span aria-hidden="true">→</span>
            </a>
          ` : ''}
        </aside>
      `;
    }

    function applyYearPlanDefault() {
      const yearPlan = root.querySelector('[data-ed-lg-year-plan]');
      if (!yearPlan) return;
      yearPlan.open = window.matchMedia('(max-width: 749px)').matches
        ? Boolean(config.yearPlanOpenMobile)
        : Boolean(config.yearPlanOpenDesktop);
      const summary = yearPlan.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', yearPlan.open ? 'true' : 'false');
    }

    function renderCurrentStep(program, steps, step, nextStep, monthName) {
      const status = currentProductStatus(step);
      const product = status.displayProduct || {};
      const isAlternative = status.type === 'acceptable';
      const rate = isAlternative ? '' : step.applicationRate;
      const description = isAlternative
        ? (step.alternativeDescription || step.description)
        : step.description;
      const recommendationHeading = status.type === 'starter'
        ? (config.starterRecommendationHeading || 'Recommended Now on Established Lawns')
        : status.type === 'different'
          ? (config.differentHeading || 'Our Recommendation:')
          : '';
      return `
        ${recommendationHeading ? `
          <div class="ed-fertilizer-guide__recommendation-head">
            <h3>${escapeHtml(recommendationHeading)}</h3>
          </div>
        ` : ''}

        <article class="ed-fertilizer-guide__product">
          ${renderProductImage(product)}
          <div>
            ${step.goal ? `<div class="ed-fertilizer-guide__goal">${escapeHtml(step.goal)}</div>` : ''}
            <h4>${escapeHtml(product.title || step.customerTitle || step.name)}</h4>
            ${rate ? `<p class="ed-fertilizer-guide__rate">Apply ${escapeHtml(rate)}</p>` : ''}
            ${isAlternative ? "<p class='ed-fertilizer-guide__rate'>Follow this product's label for the correct application rate.</p>" : ''}
            ${description ? `<p class="ed-fertilizer-guide__description">${escapeHtml(description)}</p>` : ''}
            ${step.applicationTiming ? `<p class="ed-fertilizer-guide__timing">${escapeHtml(step.applicationTiming)}</p>` : ''}
            ${renderActions(status, step)}
          </div>
        </article>

        <div class="ed-fertilizer-guide__result-lower">
          <div class="ed-fertilizer-guide__result-disclosures">
            ${renderAlternatives(step, status)}
          </div>
          ${renderPlanDetails(steps, step, nextStep)}
          ${renderProgramCta()}
        </div>
      `;
    }

    function renderOffSeason(program, steps, nextStep, monthName) {
      const currentProductStep = findCurrentProductStep(steps);
      const currentProductNote = currentProductStep && data.currentProduct
        ? `<p class="ed-fertilizer-guide__current-product-note">${escapeHtml(data.currentProduct.title)} is scheduled for ${escapeHtml(monthLabelForStep(currentProductStep, false))} in this lawn plan.</p>`
        : '';

      return `
        <div class="ed-fertilizer-guide__now-head">
          <h3>${escapeHtml(config.noFertilizerHeading || 'No fertilizer is needed right now.')}</h3>
        </div>
        <div class="ed-fertilizer-guide__empty">
          <h3>Give your lawn a break.</h3>
          <p>${escapeHtml(program.offSeasonMessage || 'No scheduled fertilizer application is recommended for this lawn right now. Follow the next step when its application window begins.')}</p>
        </div>
        <div class="ed-fertilizer-guide__result-lower">
          <div class="ed-fertilizer-guide__result-disclosures">
            ${currentProductNote}
          </div>
          ${renderPlanDetails(steps, null, nextStep)}
          ${renderProgramCta()}
        </div>
      `;
    }

    function renderNoProgram(monthName) {
      const support = config.supportLink
        ? `<a class="ed-fertilizer-guide__button" href="${escapeAttribute(config.supportLink)}">Contact Our Lawn Experts</a>`
        : '';

      return `
        <div class="ed-fertilizer-guide__now-head">
          <h3>We need one more detail.</h3>
        </div>
        <div class="ed-fertilizer-guide__empty">
          <h3>This exact lawn plan is not available yet.</h3>
          <p>Our team can confirm the best fertilizer and timing for your lawn type and location.</p>
          ${support ? `<div class="ed-fertilizer-guide__actions">${support}</div>` : ''}
        </div>
      `;
    }

    function renderResult(optionsArg) {
      if (!selectedOption || !selectedState) return;

      const monthName = currentMonthName();
      const program = resolveProgram(selectedOption, selectedState);
      const steps = program ? applicableSteps(program, selectedState) : [];
      const currentStep = program ? findCurrentStep(steps, monthName) : null;
      const nextStep = program ? findNextStep(steps, currentStep, monthName) : null;
      const currentStatus = currentStep ? currentProductStatus(currentStep) : null;
      const currentProductStep = currentStatus && currentStatus.type === 'different'
        ? findCurrentProductStep(steps)
        : null;
      const currentProductNote = currentProductStep && data.currentProduct
        ? `${data.currentProduct.title} is part of this lawn plan in ${monthLabelForStep(currentProductStep, false)}.`
        : '';
      const headerStatus = currentStep
        ? `
          <div class="ed-fertilizer-guide__status-row">
            ${renderStatus(currentStatus, true, currentProductNote)}
          </div>
        `
        : '';
      const header = `
        <div class="ed-fertilizer-guide__result-head">
          <div class="ed-fertilizer-guide__result-summary">
            <p class="ed-fertilizer-guide__result-kicker">Your Recommendation</p>
            <h3 class="ed-fertilizer-guide__plan-title" data-ed-lg-focus>${escapeHtml(selectedOption.label)}</h3>
            <div class="ed-fertilizer-guide__plan-meta">
              <p class="ed-fertilizer-guide__location">&#8982; ${escapeHtml(selectedState.name)}</p>
              <span class="ed-fertilizer-guide__month">Plan for ${escapeHtml(monthName)}</span>
            </div>
          </div>
          <div class="ed-fertilizer-guide__result-aside">
            <button type="button" class="ed-fertilizer-guide__edit" data-ed-lg-edit>Change</button>
            ${headerStatus}
          </div>
        </div>
      `;

      if (!program) {
        resultMount.innerHTML = header + renderNoProgram(monthName);
        showScreen('result', optionsArg);
        saveSelection();
        trackGuideEvent('lawn_guide_recommendation_viewed', {
          grass_type: selectedOption.label,
          state: selectedState.code,
          recommendation_status: 'no_program',
          recommendation_product_id: '',
          recommendation_product_title: '',
          month: monthName
        });
        return;
      }

      const body = currentStep
        ? renderCurrentStep(program, steps, currentStep, nextStep, monthName)
        : renderOffSeason(program, steps, nextStep, monthName);

      resultMount.innerHTML = header + body;
      showScreen('result', optionsArg);
      applyYearPlanDefault();
      saveSelection();

      const resultStatus = currentStep
        ? currentProductStatus(currentStep)
        : { type: 'off_season', displayProduct: null };
      trackGuideEvent('lawn_guide_recommendation_viewed', {
        grass_type: selectedOption.label,
        state: selectedState.code,
        recommendation_status: resultStatus.type,
        recommendation_product_id: productId(resultStatus.displayProduct),
        recommendation_product_title: resultStatus.displayProduct ? resultStatus.displayProduct.title : '',
        month: monthName
      });
    }

    function setActionFeedback(message, type) {
      let feedback = resultMount.querySelector('[data-ed-lg-action-feedback]');

      if (!feedback) {
        feedback = document.createElement('p');
        feedback.dataset.edLgActionFeedback = '';
        feedback.setAttribute('role', 'status');
        feedback.setAttribute('aria-live', 'polite');

        const actions = resultMount.querySelector('.ed-fertilizer-guide__product .ed-fertilizer-guide__actions');
        if (actions) {
          actions.insertAdjacentElement('afterend', feedback);
        } else {
          resultMount.appendChild(feedback);
        }
      }

      feedback.className = `ed-fertilizer-guide__action-feedback${type ? ` is-${type}` : ''}`;
      feedback.textContent = message || '';
      feedback.hidden = !message;
    }

    function preserveGuideScroll(scrollX, scrollY) {
      const restore = () => {
        if (Math.abs(window.scrollY - scrollY) > 2 || Math.abs(window.scrollX - scrollX) > 2) {
          window.scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
        }
      };

      window.requestAnimationFrame(() => window.requestAnimationFrame(restore));
      window.setTimeout(restore, 80);
      window.setTimeout(restore, 280);
    }

    function triggerMainProductAdd(trigger) {
      setActionFeedback('', '');

      const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'))
        .filter((form) => !root.contains(form) && form.querySelector('[name="id"]'));
      const form = forms.find((candidate) => candidate.offsetParent !== null) || forms[0];

      if (!form) {
        setActionFeedback('We could not find the product form. Please use the Add to Cart button near the top of this product page.', 'error');
        return;
      }

      const submit = form.querySelector('[name="add"], button[type="submit"], input[type="submit"]');
      if (submit && submit.disabled) {
        setActionFeedback('Choose a bag size or available option above, then try Add to Cart again.', 'error');
        return;
      }

      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      const originalLabel = trigger ? trigger.textContent : '';

      if (trigger) {
        trigger.disabled = true;
        trigger.setAttribute('aria-busy', 'true');
        trigger.textContent = 'Adding...';
      }

      try {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(submit || undefined);
        } else if (submit) {
          submit.click();
        } else {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }

        preserveGuideScroll(scrollX, scrollY);
      } catch (error) {
        console.warn('[Lawn Guide] Unable to submit the product form.', error);
        setActionFeedback('We could not add the product from this section. Please use the Add to Cart button near the top of the page.', 'error');
      } finally {
        window.setTimeout(() => {
          if (!trigger) return;
          trigger.disabled = false;
          trigger.removeAttribute('aria-busy');
          trigger.textContent = originalLabel || (config.correctCtaLabel || 'Add to Cart');
        }, 650);
      }
    }

    function selectGrass(option) {
      selectedOption = option;
      options.forEach((item) => {
        const selected = item === option;
        item.button.classList.toggle('is-selected', selected);
        item.button.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      trackGuideEvent('lawn_guide_grass_selected', {
        grass_type: option.label,
        grass_key: option.key,
        selection_type: option.behavior
      });

      if (option.behavior === 'unsure') {
        showScreen('unsure', { alignMobile: true });
        return;
      }

      if (selectedState) stateInput.value = selectedState.name;
      if (stateError) stateError.textContent = '';
      showScreen('state', { alignMobile: true });
    }

    function selectState(state) {
      selectedState = state;
      stateInput.value = state.name;
      if (stateError) stateError.textContent = '';
      trackGuideEvent('lawn_guide_state_selected', {
        grass_type: selectedOption ? selectedOption.label : '',
        state: state.code,
        state_name: state.name,
        lawn_region: regionForState(state.code)
      });
      renderResult({ alignMobile: true });
    }

    grassButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const option = options.find((item) => item.button === button);
        if (option) selectGrass(option);
      });
    });

    function submitStateSelection() {
      const state = stateFromValue(stateInput.value);

      if (!state) {
        if (stateError) {
          stateError.textContent = stateInput.value.trim()
            ? 'Please choose a state from the list.'
            : 'Please select your state.';
        }
        stateInput.focus({ preventScroll: true });
        return;
      }

      selectState(state);
    }

    stateInput.addEventListener('input', () => {
      if (stateTimer) window.clearTimeout(stateTimer);
      selectedState = null;
      if (stateError) stateError.textContent = '';
    });

    stateInput.addEventListener('change', () => {
      if (stateError) stateError.textContent = '';
    });

    stateInput.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      submitStateSelection();
    });

    stateSubmit.addEventListener('click', submitStateSelection);

    stateQuickButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const state = stateFromValue(button.dataset.state || button.textContent);
        if (state) selectState(state);
      });
    });

    root.addEventListener('click', (event) => {
      const disclosureSummary = event.target.closest(
        '.ed-fertilizer-guide__alternatives > summary, .ed-fertilizer-guide__year > summary'
      );
      if (disclosureSummary) {
        event.preventDefault();
        const disclosure = disclosureSummary.parentElement;
        disclosure.open = !disclosure.open;
        disclosureSummary.setAttribute('aria-expanded', disclosure.open ? 'true' : 'false');

        if (disclosure.classList.contains('ed-fertilizer-guide__year')) {
          trackGuideEvent('lawn_guide_plan_toggled', {
            action: disclosure.open ? 'opened' : 'closed',
            grass_type: selectedOption ? selectedOption.label : '',
            state: selectedState ? selectedState.code : ''
          });
        }
        return;
      }

      const back = event.target.closest('[data-ed-lg-back]');
      if (back) {
        const target = back.dataset.edLgBack || 'grass';
        showScreen(target, { alignMobile: true });
        return;
      }

      if (event.target.closest('[data-ed-lg-edit]')) {
        clearSavedSelection();
        showScreen('grass', { alignMobile: true });
        return;
      }

      const addCurrent = event.target.closest('[data-ed-lg-add-current]');
      if (addCurrent) {
        event.preventDefault();
        event.stopPropagation();
        trackGuideEvent('lawn_guide_cta_click', {
          cta_action: 'add_current_product',
          cta_label: addCurrent.textContent.trim(),
          recommendation_status: addCurrent.dataset.edLgResultStatus || '',
          recommendation_product_id: addCurrent.dataset.edLgProductId || '',
          recommendation_product_title: addCurrent.dataset.edLgProductTitle || ''
        });
        triggerMainProductAdd(addCurrent);
        return;
      }

      const productLink = event.target.closest('[data-ed-lg-product-link]');
      if (productLink) {
        trackGuideEvent('lawn_guide_cta_click', {
          cta_action: productLink.closest('.ed-fertilizer-guide__product') ? 'view_recommended_product' : 'view_related_product',
          cta_label: productLink.textContent.trim(),
          recommendation_status: productLink.dataset.edLgResultStatus || '',
          recommendation_product_id: productLink.dataset.edLgProductId || '',
          recommendation_product_title: productLink.dataset.edLgProductTitle || '',
          destination: productLink.pathname || ''
        });

        if (isSamePageUrl(productLink.href)) event.preventDefault();
      }
    });

    updateProgress('grass');

    if (config.rememberAnswers) {
      try {
        const saved = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
        const savedOption = saved && options.find((option) => option.key === saved.optionKey);
        const savedState = saved && STATE_LOOKUP.get(normalize(saved.stateCode));

        if (savedOption && savedState && savedOption.behavior !== 'unsure') {
          selectedOption = savedOption;
          selectedState = savedState;
          options.forEach((item) => {
            const selected = item === savedOption;
            item.button.classList.toggle('is-selected', selected);
            item.button.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });
          stateInput.value = savedState.name;
          renderResult({ immediate: true, skipFocus: true });
        }
      } catch (error) {
        clearSavedSelection();
      }
    }
  }

  function initAll(scope) {
    const context = scope || document;
    context.querySelectorAll('.ed-fertilizer-guide').forEach(initGuide);
  }

  window[GLOBAL_KEY] = { initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target || document);
  });
})();
